import { Geometry, Point, Polygon } from 'geojson';
import { groupBy } from 'lodash';

import { getMonthStart, getQuarterStart, getYearEnd, LocaleDateVariant } from '@methanesat/ui-components';

import {
    AreaEmissionsProducts,
    CaptureFeatureProperties,
    DppPlumeProperties,
    isDPPCaptureFeature,
    isDPPPlumeProps,
    TotalCaptureFluxProperties,
    TargetCaptureFluxProperties,
    NonDppPlumeProperites,
    PlumeProperties,
    RasterPointFeature,
    StacFeature,
    StacDateBlock,
    TiTilerResponse,
    STACCollection,
    CapturePlusCount,
    StacSearchResponse
} from '../../types';
import { getProductFromCollectionId } from './stacNameTransformations';
/**
 * These MUST be imported from consts/targets, not the consts/ directory or it breaks this module's
 * test file. Not totally sure why, but suspect it's related to a circular-dependency that
 * madge isn't catching.
 */
import { TARGET_GROUP_LOOKUP_BY_CHILD, TARGET_GROUPS } from '../../consts/targets';

export const captureFeaturesToDateBlocks = (
    features: StacFeature<Point | Polygon>[],
    timespan: LocaleDateVariant = LocaleDateVariant.quarterly
): Record<string, StacDateBlock[]> => {
    const processedFeatures: StacDateBlock[] = features.map((f) => {
        const captureDate = f.properties?.datetime;
        const { totalKgHr } = getStacCaptureEmissions(f.properties as CaptureFeatureProperties);
        const coordinates = f.geometry.type === 'Polygon' ? f.geometry.coordinates[0] : null;
        return {
            collection: f.collection,
            coordinates: coordinates!,
            date: new Date(captureDate),
            feature: f,
            id: f.id,
            platform: f.properties?.instrument,
            totalKgHr
        };
    });
    const getStartDate = (date: Date) => {
        switch (timespan) {
            case LocaleDateVariant.shortMonthYear:
                return getMonthStart(date);
            case LocaleDateVariant.quarterly:
                return getQuarterStart(date);
            case LocaleDateVariant.year:
                return getYearEnd(date);
        }
    };
    // group by timespan
    const groupedByTimespan = groupBy(processedFeatures, (f) => {
        return getStartDate(f.date);
    });

    return groupedByTimespan;
};

/**
 * This function gets total, upper & lower bounds for a capture's emissions.
 * Used to access the same data for both DPP- and non-DPP-generated data,
 * which use different properties.
 */
export const getStacCaptureEmissions = (
    properties: CaptureFeatureProperties
): { totalKgHr: number; upperBounds?: number; lowerBounds?: number } => {
    if (!isDPPCaptureFeature(properties)) {
        // Non-DPP capture (e.g., MAIR)
        return {
            totalKgHr: properties?.net_total,
            upperBounds: properties?.net_total_high,
            lowerBounds: properties?.net_total_low
        };
    }

    const p = properties as CaptureFeatureProperties<TotalCaptureFluxProperties & TargetCaptureFluxProperties>;
    return {
        totalKgHr: p.target_total_flux_mean ?? p.mean_total_flux,
        upperBounds: p.target_total_flux_upper_bound ?? p.total_flux_upper_bound,
        lowerBounds: p.target_total_flux_lower_bound ?? p.total_flux_lower_bound
    };
};

export const getStacPlumeTimes = (properties: PlumeProperties<DppPlumeProperties | NonDppPlumeProperites>) => {
    if (isDPPPlumeProps(properties)) {
        return {
            startTime: properties.time_coverage_start,
            endTime: properties.time_coverage_end
        };
    }
    return {
        startTime: properties.start_datetime,
        endTime: properties.end_datetime
    };
};

/**
 * Takes an array of STAC features and groups them by the MethaneSAT `collection_id` property.
 *
 * Returns an object for each grouping in which the key is the `collection_id`, and the value is an object with a
 * datetime, target ID, and an array of point and portal STAC items.
 */
export const groupFeaturesByMSATCollection = (features: StacFeature<Polygon, CaptureFeatureProperties>[]) => {
    const groupedByMSATCollection = groupBy(features, (f) => f.properties?.collection_id);

    return Object.fromEntries(
        Object.entries(groupedByMSATCollection).map(([collectionId, stacItems]) => [
            collectionId,
            {
                datetime: stacItems?.[0].properties.datetime,
                targetOrGroupId: stacItems?.[0].properties.target_group_id || stacItems?.[0].properties.target_id,
                stacItems: stacItems
            }
        ])
    );
};

/**
 * Takes an array of STAC features, and returns an array that contains only the most recent processing
 * for each MSAT collection.
 *
 * For example, for MSAT collection ID `01FA0690`, only the latest `Level3_Portal`, `Level4_Portal`,
 * and `Level4_point_pruned` STAC items are kept.
 */
export const getLatestProcessedFeatures = <G extends Geometry = Polygon>(
    features: StacFeature<G, CaptureFeatureProperties>[] | undefined
) => {
    const allLatestFeatures: StacFeature<G, CaptureFeatureProperties>[] = [];

    // First, group features by MSAT Collection IDs
    const featuresByMSATCollection: { [collectionId: string]: StacFeature<G, CaptureFeatureProperties>[] } = groupBy(
        features,
        (feature) => feature.properties?.collection_id
    );

    Object.values(featuresByMSATCollection).forEach((features) => {
        // Group the features for each MSAT collection (e.g. 01FA0690) by STAC collection ID (e.g. MethaneSAT_Level4_Portal)
        // group MethaneSAT_Level4_Portal & MethaneSAT_Level4_Portal_twostep together
        const featuresBySTACCollection: {
            [stacCollection: string]: StacFeature<G, CaptureFeatureProperties>[];
        } = groupBy(features, (feature) => {
            if (
                feature.collection === STACCollection.MethaneSAT_Level4_Portal ||
                feature.collection === STACCollection.MethaneSAT_Level4_Portal_twostep
            )
                return STACCollection.MethaneSAT_Level4_Portal;
            return feature.collection;
        });

        // For each STAC collection, keep only the collection that was most recently processed (by the `created` property)
        Object.values(featuresBySTACCollection).forEach((featuresForStacCollection) => {
            const sortedByProcessingDate = featuresForStacCollection.sort((a, b) => {
                const dateA = isDPPCaptureFeature(a.properties) ? new Date(a.properties.created).getTime() : -Infinity;
                const dateB = isDPPCaptureFeature(b.properties) ? new Date(b.properties.created).getTime() : -Infinity;
                return dateB - dateA;
            });

            // keep only the latest
            if (sortedByProcessingDate[0]) allLatestFeatures.push(sortedByProcessingDate[0]);
        });
    });
    return allLatestFeatures;
};

/**
 * Removes the `_point_pruned` STAC items from an array of CapturePlusCount if the corresponding `_Portal`
 * STAC items exist in that array. If only the `_point_pruned` item exists, then it is kept.
 *
 * This prevents drawing duplicate targets for captures that have items in both STAC collections,
 * and lets us draw empty targets for captures that only have point sources.
 */
export const filterPointCapturesIfParentPresent = (captures: CapturePlusCount[]) => {
    const portalL3Captures: CapturePlusCount[] = [];
    const portalL4Captures: CapturePlusCount[] = [];
    const pointCaptures: CapturePlusCount[] = [];

    captures.forEach((capture) => {
        if (capture.collection.includes('point')) {
            pointCaptures.push(capture);
        } else if (getProductFromCollectionId(capture.collection) === AreaEmissionsProducts.l3) {
            portalL3Captures.push(capture);
        } else {
            portalL4Captures.push(capture);
        }
    });

    // TODO: Once we've got more L3 data (more than there is for L4, which is currently not the case)
    // we need to filter the L3 items for only those that have L4 products
    return [...portalL3Captures, ...portalL4Captures];
};

/**
 * This function takes all the STAC features and does the following:
 * * Filters out captures that only contain point sources and no matching COG
 * * Filters out L3 COGs that don't have corresponding L4 COGs
 */
export const filterToCapturesAndPointsWithMatchingTotals = <G extends Geometry = Polygon>(
    features: StacFeature<G, CaptureFeatureProperties>[] | undefined
) => {
    const cogCaptures: StacFeature<G, CaptureFeatureProperties>[] = [];
    const pointCaptures: StacFeature<G, CaptureFeatureProperties>[] = [];

    features?.forEach((feature) => {
        const { totalKgHr } = getStacCaptureEmissions(feature.properties);
        if (feature.collection.includes('point')) {
            pointCaptures.push(feature);
        } else if (feature.collection.includes('Level3_Portal')) {
            cogCaptures.push(feature);
        } else if (totalKgHr) {
            cogCaptures.push(feature);
        }
    });

    // Separate L3 and L4 area captures
    const L3AreaCaptures = cogCaptures.filter(
        (f) =>
            f.collection === STACCollection.MethaneAIR_Level3_Portal ||
            f.collection === STACCollection.MethaneSAT_Level3_Portal
    );
    const L4AreaCaptures = cogCaptures.filter(
        (f) =>
            f.collection === STACCollection.MethaneAIR_Level4_Portal ||
            f.collection === STACCollection.MethaneSAT_Level4_Portal ||
            f.collection === STACCollection.MethaneSAT_Level4_Portal_twostep
    );

    // Build a set of L4 area MSAT collection_ids
    const L4AreaCollectionIdSet = new Set(L4AreaCaptures.map((f) => f.properties.collection_id));

    const L3CapturesToUse = L3AreaCaptures.filter(
        (f) => f.properties && L4AreaCollectionIdSet.has(f.properties.collection_id)
    );

    // For point captures, keep only those with matching COG captures
    const cogCaptureCollectionIds = cogCaptures.map((c) => c.properties.collection_id);
    const pointCapturesWithMatchingCogCapture = pointCaptures.filter((c) =>
        cogCaptureCollectionIds.includes(c.properties.collection_id)
    );

    return [...L3CapturesToUse, ...L4AreaCaptures, ...pointCapturesWithMatchingCogCapture];
};

/**
 * Filters L4 captures to include only those that have a corresponding
 * L3 capture with the same `collection_id`.
 *
 * @param L4Captures - An array of Level 4 captures (belonging to either the
 * `Level4_Portal` or `Level4_point_pruned` collections)
 *
 * @param L3Captures - An array of Level 3 captures (belonging to the `Level3_Portal` collections)
 *
 * @returns An array of L4 captures whose `collection_id` matches any of the
 * `collection_id`s present in the L3 captures.
 */
export const filterL4CapturesWithL3 = (L4Captures: CapturePlusCount[], L3Captures: CapturePlusCount[]) => {
    const L3CollectionIds = new Set(L3Captures?.map((item) => item.properties.collection_id));
    return L4Captures.filter((capture) => L3CollectionIds.has(capture.properties.collection_id));
};

/**  Adds the STAC item id of the parent target (from the `Portal` collection) to each point item as a property */
export const addRelatedRasterItemIdToPointCaptures = (captures: CapturePlusCount[]) => {
    const featuresByCollection = groupBy(captures, (capture) => capture.properties?.collection_id);

    for (const captures of Object.values(featuresByCollection)) {
        if (captures.length === 1) continue;

        const [first, second] = captures;
        if (first.collection.includes('point')) {
            first.relatedRasterItemId = second.id;
        } else {
            second.relatedRasterItemId = first.id;
        }
    }
    return captures;
};

/**
 * Adds the group id from `consts/targets.ts` as `target_group_id` to each STAC item, if available.
 * Collects group geometries and adds them to each STAC item, if applicable.
 */
export const addTargetGroupDataToStacItems = <G extends Geometry = Polygon>(
    captures: StacFeature<G, CaptureFeatureProperties>[] | undefined
): StacFeature<G, CaptureFeatureProperties>[] | undefined => {
    if (!captures) return captures;

    /** targetGroupGeometries: Map<targetGroupId, targetGeometry> */
    const targetGroupGeometries = new Map(
        Array.from(Object.keys(TARGET_GROUPS), (targetGroupId) => {
            const captureInTarget = captures.find(
                (c) => c.properties.target_id === targetGroupId && !!c.properties.target_geometry
            );
            return [targetGroupId, captureInTarget?.properties.target_geometry];
        })
    );

    return captures.map((capture) => {
        const targetId = capture.properties.target_id;
        const targetGroupId = TARGET_GROUP_LOOKUP_BY_CHILD[targetId] || null;
        const targetGroupGeometry =
            targetGroupId && targetGroupGeometries.has(targetGroupId) ? targetGroupGeometries.get(targetGroupId) : null;

        return {
            ...capture,
            properties: {
                ...capture.properties,
                ...(targetGroupId && { target_group_id: targetGroupId }),
                ...(targetGroupGeometry && { target_group_geometry: targetGroupGeometry })
            }
        };
    });
};

/**
 * Formats a single STAC feature and its corresponding raster data into a `RasterPointFeature` GeoJSON object.
 */
export const formatRasterPointFeature = (
    feature: StacFeature,
    rasterData: TiTilerResponse,
    coordinates: number[]
): RasterPointFeature => {
    //
    // COGs produced by the DPP contain 3 bands: mean flux, lower-bound flux, upper-bound flux.
    // If a COG is missing lower and/or upper bounds, the missing bands will simply be undefined.
    //
    // COGs from the two-step process contain a fourth band of data indicating whether or not a given cell
    // has been discarded after the first step. 1 = included, 0 = discarded.
    //
    const [methaneValue, methaneLowerBound, methaneUpperBound, pixel] = rasterData.values;
    const targetGroupId = TARGET_GROUP_LOOKUP_BY_CHILD[feature.properties?.target_id] || null;
    return {
        id: feature.id,
        type: 'Feature',
        collection: feature.collection,
        geometry: {
            coordinates: coordinates,
            type: 'Point'
        },
        properties: {
            collectionEndTime: feature.properties?.end_datetime,
            collectionStartTime: feature.properties?.start_datetime,
            source: feature.properties?.instrument,
            collectionId: feature.collection,
            target_id: feature.properties?.target_id,
            ...(targetGroupId && { target_group_id: targetGroupId }),
            sceneId: feature.properties?.basin,
            methane: methaneValue,
            methaneLowerBound,
            methaneUpperBound,
            ...(pixel !== undefined && { pixelDiscarded: !pixel }),
            pixelSizeKm2: feature.properties?.pixel_size_km2,
            processorVersion: feature.properties?.processor_version
        }
    };
};

/**
 * Normalizes target_id's to strings for all features in a STAC search response
 * in order to maintain consistency throughout the app and avoid potential type mismatches.
 *
 * Mutates in place since it's always called on freshly parsed API responses.
 *
 * @param features Array of STAC items returned from a STAC search
 */
export const normalizeTargetIds = (features: StacSearchResponse['features']) => {
    features?.forEach((f) => {
        if (f.properties?.target_id != null) {
            f.properties.target_id = String(f.properties.target_id);
        }
    });
};
